import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Toggle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { SquareDashedIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DetectedObject } from '@/entities/brand-media';

import { BoundingBoxOverlay } from './BoundingBoxOverlay';

/**
 * The picture at the size a box is actually checkable against, with the same boxes the panel draws.
 *
 * The frame shrink-wraps the picture rather than being given a ratio of its own. That is what makes
 * a box land exactly on the fractions it was stored with: the element the overlay measures against
 * IS the image, so there is no crop and no letterbox for a projection to undo.
 *
 * It used to take its ratio from the recorded `width`/`height`, falling back to 4:3 when a row had
 * none. That fallback drew every box in the wrong place: the browser still letterboxed the image by
 * its real ratio inside a 4:3 frame, while the projection was told the two matched and scaled
 * nothing. Nothing ties `objects` to those columns, so a partial backfill or a direct write is
 * enough to hit it. Shrink-wrapping removes the case rather than adding a second fallback to it.
 */
export const MediaPreviewDialog = ({
  open,
  title,
  url,
  width,
  height,
  objects,
  boxesShown,
  onBoxesShownChange,
  onOpenChange,
}: {
  open: boolean;
  title: string;
  url: string;
  /** The picture's own pixels. Absent for a row nothing measured, which falls back to the frame's. */
  width: number | null;
  height: number | null;
  objects: readonly DetectedObject[];
  boxesShown: boolean;
  onBoxesShownChange: (shown: boolean) => void;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation('tools');

  // Only ever used to satisfy `projectBox`, which is an identity when the two ratios agree -- and
  // they agree by construction here, because the frame is the image. Read from the recorded columns
  // when they are there and from the loaded picture when they are not, so neither path can disagree
  // with what is on screen.
  const [loadedAspect, setLoadedAspect] = useState<number | null>(null);
  const [measuredFor, setMeasuredFor] = useState(url);
  if (measuredFor !== url) {
    setMeasuredFor(url);
    setLoadedAspect(null);
  }

  const recorded = width != null && height != null && height !== 0 ? width / height : null;
  const aspect = recorded ?? loadedAspect ?? 1;
  const drawn = boxesShown ? objects.map((_object, index) => index) : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[92vw] sm:max-w-[92vw]">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 pr-6">
            <DialogTitle className="truncate">{title}</DialogTitle>
            {objects.length > 0 && (
              // Its own provider rather than the app's: a dialog portals out of this tree, and a
              // tooltip whose provider is missing throws rather than degrading.
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Toggle
                      size="sm"
                      pressed={boxesShown}
                      onPressedChange={onBoxesShownChange}
                      aria-label={t('brandMedia.objects.toggle')}
                      data-testid="dialog-boxes-toggle"
                    >
                      <SquareDashedIcon />
                    </Toggle>
                  </TooltipTrigger>
                  <TooltipContent>{t('brandMedia.objects.toggle')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </DialogHeader>

        <div className="flex justify-center">
          <div className="relative inline-block overflow-hidden" data-testid="dialog-preview-frame">
            <img
              src={url}
              alt=""
              className="block max-h-[78vh] max-w-full"
              onLoad={(event) => {
                const { naturalWidth, naturalHeight } = event.currentTarget;
                if (naturalHeight > 0) {
                  setLoadedAspect(naturalWidth / naturalHeight);
                }
              }}
            />
            {/* The same number twice on purpose: the frame is the image, so there is nothing to
                project and `projectBox` passes the fractions straight through. */}
            <BoundingBoxOverlay
              objects={objects}
              drawn={drawn}
              imageAspect={aspect}
              elementAspect={aspect}
              fit="contain"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
